import type { ReactNode } from 'react';
import { useI18n } from '../i18n/useI18n';
import { Logo } from './icons';

/**
 * The two-column frame both sign-in screens use.
 *
 * Both entry points sign people in, and they sign them in through entirely
 * different things — a provider account on one side, a tenancy's own identity
 * provider on the other. What they share is the page around the form, and it
 * lives here so that the difference between them is only ever the card.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const { t } = useI18n();

  return (
    <div className="login">
      <aside className="login-aside">
        <div className="glow" />
        <div className="brand-row">
          <Logo />
          <span className="brand-name">{t('common.brand')}</span>
        </div>
        <div className="login-quote">
          <p>{t('login.quote')}</p>
          <p className="by">{t('login.quote.by')}</p>
        </div>
        <div className="login-terminal" aria-hidden="true">
          <div className="tl">
            <span className="mut">{t('login.terminal.comment')}</span>
          </div>
          <div className="tl">
            <span className="pr">{'$ '}</span>
            {t('login.terminal.login')} <span className="ok">{'✓'}</span>
          </div>
          <div className="tl">
            <span className="pr">{'$ '}</span>
            {t('login.terminal.ssh')} <span className="mut">{t('login.terminal.ssh.note')}</span>
          </div>
          <div className="tl">
            <span className="pr">{'$ '}</span>
            {t('login.terminal.psql')} <span className="mut">{t('login.terminal.psql.note')}</span>
          </div>
          <div className="tl">
            <span className="pr">{'$ '}</span>
            {t('login.terminal.kubectl')}{' '}
            <span className="mut">{t('login.terminal.kubectl.note')}</span>
          </div>
        </div>
      </aside>

      <main className="login-main">
        <div className="login-card">
          <div className="brand-row" style={{ marginBottom: 26 }}>
            <Logo />
            <span className="brand-name">
              {t('common.brand')} <span className="soft">{t('common.console')}</span>
            </span>
          </div>

          {children}

          <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center' }}>
          </div>
        </div>
      </main>
    </div>
  );
}

/** A dead end: signed in, nothing to open. No navigation to hide, none shown. */
export function NoticeScreen({ children }: { children: ReactNode }) {
  const { t } = useI18n();

  return (
    <main className="login-main" style={{ minHeight: '100vh' }}>
      <div className="login-card">
        <div className="brand-row" style={{ marginBottom: 26 }}>
          <Logo />
          <span className="brand-name">
            {t('common.brand')} <span className="soft">{t('common.console')}</span>
          </span>
        </div>

        {children}

        <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center' }}>
        </div>
      </div>
    </main>
  );
}
